import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Snackbar,
  Alert,
  styled,
  CircularProgress
} from '@mui/material';
import { createSubscription } from '../../services/api';
import { SnackbarProvider, enqueueSnackbar } from 'notistack';

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const SubscriptionForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid email address',
        severity: 'error',
      });
      return;
    }

    try {
      setIsLoading(true);
      
      const { statusText } = await createSubscription(formData);
      enqueueSnackbar('Thank you for subscribing!', { variant: 'success' });
      // Clear form
      setFormData({ name: '', email: '' });
      
    } catch (error) {
      console.log({error});
      enqueueSnackbar((error as any).response.data.message, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ py: 6, backgroundColor: 'background.paper' }}>
      <Container maxWidth="sm">
        <Typography
          variant="h3"
          component="h2"
          align="center"
          sx={{
            mb: 1,
            fontFamily: "'Playfair Display', serif",
            fontStyle: 'italic',
          }}
        >
          Don't Miss Out
        </Typography>
        <Typography
          variant="h4"
          component="h3"
          align="center"
          sx={{
            mb: 3,
            fontFamily: "'Playfair Display', serif",
          }}
        >
          SUBSCRIBE
        </Typography>
        <Typography
          variant="body1"
          align="center"
          sx={{ mb: 4 }}
        >
          Sign up to be the FIRST to get life updates, new finds, and exclusive access.
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <StyledTextField
            fullWidth
            name="name"
            placeholder="Your Name"
            value={formData.name}
            onChange={handleChange}
            required
            variant="outlined"
          />
          <StyledTextField
            fullWidth
            name="email"
            type="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={handleChange}
            required
            variant="outlined"
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              py: 1.5,
              backgroundColor: '#8B5A2B',
              color: '#F8F4E9',
              '&:hover': {
                backgroundColor: '#B8860B',
              },
            }}
          >
            {isLoading ? <CircularProgress size={24} /> : 'SIGN ME UP!'}
          </Button>
        </Box>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
      <SnackbarProvider />
    </Box>
  );
};

export default SubscriptionForm; 